import { useEffect, useMemo, useRef, useState } from "react";
import VendorTagMappingHeader from "./VendorTagMappingHeader";
import GlobalFilter from "../../../Utility/GlobalFilter/GlobalFilter";
import Typography from "../../../Utility/Typography";
import Button from "../../../Utility/Button/Button";
import FloatingTooltip from "../../../Utility/FloatingTooltip/FloatingTooltip";
import { RiDeleteBinFill, RiPencilLine } from "react-icons/ri";
import { SortableHeader, useSorting } from "../../../Utility/Sorting";
import Pagination from "../../../Utility/Pagination/Pagination";
import VendorTagMappingAddModel from "./AddMappingModels/VendorTagMappingAddModel";
import AddButton from "../../../Utility/AddButton/AddButton";
import { useDispatch, useSelector } from "react-redux";
import {
  selectVendorTagMappingData,
  selectVendorTagMappingLoading,
} from "../../../Redux/Slices/vendorTagMappingSlice";
import {
  createVendorTagMapping,
  deleteVendorTagMappingApi,
  fetchVendorTagMappings,
  updateVendorTagMappingApi,
} from "../../../Redux/Thunks/vendorTagMappingThunk";
import { fetchSystemList } from "../../../Redux/Thunks/systemListsThunk";
import { selectSystemListItems } from "../../../Redux/Slices/systemListsSlice";
import { mapSystemListItemToOption } from "../../Issues/issueMappers";
import DeleteConfirmModal from "../../../Utility/DeleteConfirmModal/DeleteConfirmModal";
import { showError, showSuccess } from "../../../utils/toast";
import { ContentLoader } from "../../../Utility/Loader";
import { mappingListGlobalFilterProps } from "../../../Utility/GlobalFilter/mappingListFilterDefaults";
import { getVendorTagMappingsListFromApiData } from "./vendorTagMappingDataUtils";

const EMPTY_VENDOR_FILTERS = {
  search: "",
  tag: "",
  account: "",
};

const VendorTagMapping = () => {
  const [filters, setFilters] = useState(() => ({ ...EMPTY_VENDOR_FILTERS }));
  const [appliedFilters, setAppliedFilters] = useState(() => ({ ...EMPTY_VENDOR_FILTERS }));

  const dispatch = useDispatch();
  const vendorTagMappingData = useSelector(selectVendorTagMappingData);
  const loading = useSelector(selectVendorTagMappingLoading);
  const hasFetchedRef = useRef(false);

  const vendorTagList = useSelector((s) => selectSystemListItems(s, "VENDOR_TAG")) ?? [];
  const accountingList = useSelector((s) => selectSystemListItems(s, "ACCOUNTING_TAG")) ?? [];

  const sourceTagOptions = useMemo(
    () => vendorTagList.map((item) => mapSystemListItemToOption(item, {})),
    [vendorTagList]
  );
  const accountingTagOptions = useMemo(
    () => accountingList.map((item) => mapSystemListItemToOption(item, {})),
    [accountingList]
  );

  const tagFilterOptions = useMemo(
    () => [{ value: "", label: "All" }, ...sourceTagOptions],
    [sourceTagOptions]
  );
  const accountFilterOptions = useMemo(
    () => [{ value: "", label: "All" }, ...accountingTagOptions],
    [accountingTagOptions]
  );

  const rawMappings = getVendorTagMappingsListFromApiData(vendorTagMappingData);

  const filteredMappings = useMemo(() => {
    let list = rawMappings;
    const f = appliedFilters;
    if (f.search?.trim()) {
      const q = f.search.toLowerCase();
      list = list.filter(
        (m) =>
          String(m.tag ?? "")
            .toLowerCase()
            .includes(q) ||
          String(m.account ?? "")
            .toLowerCase()
            .includes(q)
      );
    }
    if (f.tag) {
      list = list.filter((m) => String(m.sourceTagId) === String(f.tag));
    }
    if (f.account) {
      list = list.filter((m) => String(m.targetTagId) === String(f.account));
    }
    return list;
  }, [rawMappings, appliedFilters]);

  const { sortedData, sortKey, sortDirection, handleSort } = useSorting(filteredMappings, {
    defaultSortKey: "tag",
    defaultSortDirection: "asc",
  });

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 50;

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    dispatch(
      fetchSystemList({
        listType: "VENDOR_TAG",
        context: "settings-vendor-tag-mapping",
        force: true,
      })
    );
    dispatch(
      fetchSystemList({
        listType: "ACCOUNTING_TAG",
        context: "settings-vendor-tag-acc",
        force: true,
      })
    );
    dispatch(fetchVendorTagMappings({ activeOnly: false }));
  }, [dispatch]);

  const refetch = () => dispatch(fetchVendorTagMappings({ activeOnly: false }));

  const handleFilterReset = () => {
    setFilters({ ...EMPTY_VENDOR_FILTERS });
    setAppliedFilters({ ...EMPTY_VENDOR_FILTERS });
    setCurrentPage(1);
    refetch();
  };

  const applyFilters = () => {
    setAppliedFilters({ ...filters });
    setCurrentPage(1);
  };

  const filterFields = [
    {
      type: "input",
      name: "search",
      label: "Search",
      value: filters.search || "",
      placeholder: "Search tag or account",
    },
    {
      type: "dropdown",
      name: "tag",
      label: "Vendor tag",
      value: filters.tag || "",
      options: tagFilterOptions,
      placeholder: "All",
      searchable: true,
      multiselect: false,
    },
    {
      type: "dropdown",
      name: "account",
      label: "Accounting",
      value: filters.account || "",
      options: accountFilterOptions,
      placeholder: "All",
      searchable: true,
      multiselect: false,
    },
  ];

  const totalPages = Math.max(1, Math.ceil(sortedData.length / pageSize));
  const startIndex = (currentPage - 1) * pageSize;
  const paginatedMappings = sortedData.slice(startIndex, startIndex + pageSize);

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [mappingToEdit, setMappingToEdit] = useState(null);
  const [mappingToDelete, setMappingToDelete] = useState(null);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddMapping = () => {
    setMappingToEdit(null);
    setIsAddModalOpen(true);
  };

  const handleEditMapping = (item) => {
    setMappingToEdit(item);
    setIsAddModalOpen(true);
  };

  const handleDeleteClick = (item) => {
    setMappingToDelete(item);
  };

  const handleConfirmDelete = async () => {
    if (!mappingToDelete?.id) return;
    try {
      await dispatch(deleteVendorTagMappingApi(mappingToDelete.id)).unwrap();
      showSuccess("Mapping deleted");
      setMappingToDelete(null);
      await refetch();
    } catch (e) {
      showError(e || "Failed to delete mapping");
    }
  };

  const handleSaveNewMapping = async ({ id, sourceTagId, targetTagId }) => {
    if (id) {
      await dispatch(updateVendorTagMappingApi({ id, sourceTagId, targetTagId })).unwrap();
      showSuccess("Mapping updated");
    } else {
      await dispatch(createVendorTagMapping({ sourceTagId, targetTagId })).unwrap();
      showSuccess("Mapping created");
      setCurrentPage(1);
    }
    await refetch();
    setIsAddModalOpen(false);
    setMappingToEdit(null);
  };

  const handleCloseMappingModal = () => {
    setIsAddModalOpen(false);
    setMappingToEdit(null);
  };

  return (
    <>
      <ContentLoader loading={loading} className="min-h-[calc(100vh-12rem)]">
        <div className="flex items-start justify-between">
          <VendorTagMappingHeader />
          <AddButton onClick={handleAddMapping} tooltip="Add Mapping" className="mt-4" />
        </div>

        <GlobalFilter
          {...mappingListGlobalFilterProps}
          listingFilter={filters}
          handleFilterChange={handleFilterChange}
          handleFilterReset={handleFilterReset}
          applyFilters={applyFilters}
          fields={filterFields}
        />

        <div className="w-full relative flex flex-col bg-surface-primary shadow-sm">
          <div className="flex-1 p-2">
            <div className="overflow-x-auto">
              <table className="w-full mb-0 clients-table">
                <thead className="bg-surface-secondary">
                  <tr>
                    <SortableHeader
                      columnKey="tag"
                      label="Vendor Tag"
                      onSort={handleSort}
                      currentSortKey={sortKey}
                      sortDirection={sortDirection}
                    />
                    <SortableHeader
                      columnKey="account"
                      label="Accounting Category"
                      onSort={handleSort}
                      currentSortKey={sortKey}
                      sortDirection={sortDirection}
                    />
                    <SortableHeader
                      columnKey="action"
                      label="Actions"
                      sortable={false}
                      className="action-col"
                    />
                  </tr>
                </thead>

                <tbody className="relative">
                  {loading ? (
                    <tr aria-hidden="true">
                      <td colSpan={3} className="min-h-[240px] h-60 p-0 border-0" />
                    </tr>
                  ) : sortedData.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="text-center py-6">
                        <Typography className="text-text-secondary">
                          No vendor tag mappings found
                        </Typography>
                      </td>
                    </tr>
                  ) : (
                    paginatedMappings.map((item) => (
                      <tr key={item.id}>
                        <td>
                          <button
                            type="button"
                            className="text-left w-full bg-transparent border-0 p-0 m-0 cursor-pointer"
                            onClick={() => handleEditMapping(item)}
                            aria-label={`Edit vendor tag mapping: ${item.tag}`}
                          >
                            <Typography variant="span1" className="font-medium">
                              {item.tag}
                            </Typography>
                          </button>
                        </td>
                        <td>
                          <Typography variant="span1">{item.account}</Typography>
                        </td>
                        <td className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <FloatingTooltip label="Edit">
                              <Button
                                size="sm"
                                className="btn-warning-new !w-7 !h-7 !p-0 flex items-center justify-center"
                                aria-label="Edit vendor tag mapping"
                                onClick={() => handleEditMapping(item)}
                              >
                                <RiPencilLine className="text-white text-[12px]" />
                              </Button>
                            </FloatingTooltip>
                            <FloatingTooltip label="Delete">
                              <Button
                                size="sm"
                                className="btn-danger-new !w-7 !h-7 !p-0 flex items-center justify-center"
                                aria-label="Delete vendor tag mapping"
                                onClick={() => handleDeleteClick(item)}
                              >
                                <RiDeleteBinFill className="text-white text-[12px]" />
                              </Button>
                            </FloatingTooltip>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="px-4 py-3 border-t bg-surface-secondary">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      </ContentLoader>

      {isAddModalOpen && (
        <VendorTagMappingAddModel
          onClose={handleCloseMappingModal}
          onSave={handleSaveNewMapping}
          sourceTagOptions={sourceTagOptions}
          accountingTagOptions={accountingTagOptions}
          existingMapping={mappingToEdit}
        />
      )}

      <DeleteConfirmModal
        isOpen={!!mappingToDelete}
        onClose={() => setMappingToDelete(null)}
        onConfirm={handleConfirmDelete}
        title="Delete vendor tag mapping"
        itemName={mappingToDelete ? `${mappingToDelete.tag} → ${mappingToDelete.account}` : ""}
      />
    </>
  );
};

export default VendorTagMapping;
